/* True Machines — measurement runtime.
   Registry + rules: ../../ANALYTICS.md (single source of truth). This file is the ONLY place
   that knows how a registry event maps onto Meta Pixel / GA4. Sections and the page scripts
   just call tmTrack('tm_x_y', {...}) and stay platform-agnostic.

   Loaded in <head> with defer, AFTER snippets/tm-analytics.liquid (which sets window.TM_ANALYTICS
   and the fbq/gtag base codes) and BEFORE tm-pdp.js / tm-home.js — defer preserves that order,
   so tmTrack is defined before any section script runs.

   HOW EACH EVENT IS SENT — one mental model, both platforms:
     custom name  → always sent verbatim (fbq trackCustom / gtag event). Exact registry name.
     mapped name  → also sent when the registry gives a standard/recommended mapping. That is
                    what the platforms actually optimize and report on.
   The one exception is `ga4Auto` events (see MAP): GA4's own page_view already covers them, so
   re-sending it would double-count pageviews. Meta has no such overlap (its base fires PageView
   only), so the Meta side still sends.

   NEVER rename an event here once it is live — ad-platform learning resets and history breaks.
   Deprecate in ANALYTICS.md, add the new name, run both through a learning window. */
(function () {
  'use strict';

  var CFG = window.TM_ANALYTICS || {};
  var CURRENCY = CFG.currency || 'INR';
  var LEAD_PLACEMENT_KEY = 'tm_lead_placement'; // survives the lead form's full-page POST

  /* ---------- UTM / creative attribution capture ----------
     Ad URLs land with utm_* params; carry them through the session so the lead POST can attribute the
     lead to its creative (utm_content = the Meta ad name). Session-scoped, last-utm-wins: an entry with
     NO utm (organic/direct, or an internal navigation) never overwrites → correctly attributed non-ad.
     Mirrors the sessionStorage "survives the full-page POST" pattern already used for lead placement/phone.
     Read back in tm-pdp.js / tm-home.js via window.TM.readUtm() when building the /lead body. */
  var UTM_KEY = 'tm_attr';
  var UTM_FIELDS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

  function captureUtm() {
    try {
      var qs = new URLSearchParams(location.search);
      if (!UTM_FIELDS.some(function (k) { return qs.has(k); })) return; // no utm on this URL → leave stored value intact
      var attr = { landing_path: location.pathname };
      UTM_FIELDS.forEach(function (k) { attr[k] = (qs.get(k) || '').slice(0, 128); }); // URLSearchParams decodes +→space
      sessionStorage.setItem(UTM_KEY, JSON.stringify(attr));
    } catch (e) {}
  }

  function readUtm() { // non-destructive — a lead is one-shot; keep the attribution stable across re-renders
    try { return JSON.parse(sessionStorage.getItem(UTM_KEY) || '{}'); } catch (e) { return {}; }
  }

  function log() {
    if (!CFG.debug || !window.console) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[tm-analytics]');
    console.log.apply(console, args);
  }

  function hasMeta() { return !!CFG.metaPixelId && typeof window.fbq === 'function'; }
  function hasGa4() { return !!CFG.ga4Id && typeof window.gtag === 'function'; }

  function product() { return CFG.product || {}; }

  /* Param builders — take the flat registry params, return platform-shaped payloads.
     Kept as functions (not static objects) because most need the page's product context. */
  function metaViewContent(p) {
    var pr = product();
    return {
      content_ids: [p.sku || pr.sku],
      content_type: 'product',
      content_name: pr.name,
      value: p.price != null ? p.price : pr.price,
      currency: CURRENCY
    };
  }
  function ga4ViewItem(p) {
    var pr = product();
    var price = p.price != null ? p.price : pr.price;
    return {
      currency: CURRENCY,
      value: price,
      items: [{ item_id: p.sku || pr.sku, item_name: pr.name, price: price }]
    };
  }
  /* Leads carry NO value: a pincode lead is not worth the machine's price, and a wrong value
     teaches Meta's value optimization the wrong thing. Optimize on count until real revenue
     exists. content_name keeps the two lead sources separable inside Meta. */
  function metaLead(p) {
    return { content_name: p.placement || p.slot || 'lead', content_category: CFG.page || '' };
  }
  function ga4GenerateLead(p) {
    return { currency: CURRENCY, method: p.placement || p.slot || 'lead' };
  }
  function ga4SelectItem(p) {
    return {
      item_list_name: p.placement || '',
      items: [{ item_id: p.sku || '' }]
    };
  }

  /* ---------- the map: registry row → platform sends ----------
     meta/ga4 `null` = no standard mapping in ANALYTICS.md (custom name only, diagnostic).
     ga4Auto = GA4 already emits an equivalent automatically; skip the mapped send. */
  var MAP = {
    tm_pdp_view:        { meta: 'ViewContent',  metaParams: metaViewContent, ga4: 'view_item',     ga4Params: ga4ViewItem },
    tm_lead_open:       { meta: null,           ga4: null },
    tm_lead_submit:     { meta: 'Lead',         metaParams: metaLead,        ga4: 'generate_lead', ga4Params: ga4GenerateLead },
    /* tm_lead_verified — the OTP-verified step (added 2026-07-24). Its OWN Meta custom event, NOT
       mapped to `Lead`: `Lead` stays on tm_lead_submit so its ad-learning/CAC history is unbroken
       (LEAD-FLOW-CONTRACT §4a). The tm_lead_verified→tm_lead_submit gap also measures the hCaptcha
       drop-off. Promote to a Meta Custom Conversion later if you want to optimize on verified leads. */
    tm_lead_verified:   { meta: null,           ga4: null },
    tm_home_view:       { meta: 'ViewContent',  metaParams: function () { return { content_type: 'home', currency: CURRENCY }; },
                          ga4: 'page_view',     ga4Auto: true },
    tm_explore_click:   { meta: null,           ga4: 'select_item',   ga4Params: ga4SelectItem },
    tm_call_booked:     { meta: 'Lead',         metaParams: metaLead,        ga4: 'generate_lead', ga4Params: ga4GenerateLead },
    tm_support_request: { meta: null,           ga4: null },
    tm_stage_dot_click: { meta: null,           ga4: null },
    /* Mid-funnel engagement (added 2026-07-21). Diagnostics, not optimization targets — they exist
       so the PDP funnel has a middle: view → looked at it → read the checks → asked for availability. */
    tm_gallery_view:    { meta: null,           ga4: null },
    tm_check_expand:    { meta: null,           ga4: null },
    /* Spec-sheet mobile peek toggle (added 2026-07-25, r2). Diagnostic, custom-only — same class as
       the two above. Unlike them it fires BOTH directions (`direction: expand|collapse`): the single
       spec toggle's collapse is a meaningful "the peek didn't satisfy" signal (Decision 8). */
    tm_spec_expand:     { meta: null,           ga4: null }
  };

  /* Clarity is a THIRD destination, not an ad platform: tagging the session lets heatmaps and
     recordings be filtered to "sessions that converted" / "sessions from the buy-bar CTA".
     Kept to low-cardinality tags on purpose — Clarity's filters get useless if every value is unique.
     The base tag comes from the **Clarity Shopify app**, not from our theme (see tm-analytics.liquid).
     It loads into the main page context, so `window.clarity` is a global here — verified live
     2026-07-21. The guard below means this simply no-ops if the app is ever uninstalled. */
  function clarityTag(name, params) {
    if (typeof window.clarity !== 'function') return;
    try {
      window.clarity('event', name);
      if (name === 'tm_lead_submit' || name === 'tm_call_booked') window.clarity('set', 'tm_converted', name);
      if (params.placement) window.clarity('set', 'tm_placement', params.placement);
    } catch (e) { log('clarity tag failed', name, e); }
  }

  /* ---------- the one public entry point ----------
     opts.eventID (optional) is attached to the MAPPED Meta standard event so the browser
     hit and the Conversions API hit for the same conversion carry the same id and Meta
     collapses them. If that id ever stops matching, every lead is counted twice. */
  function tmTrack(name, params, opts) {
    params = params || {};
    var row = MAP[name];
    if (!row) {
      log('UNREGISTERED event "' + name + '" — add it to ANALYTICS.md and MAP before shipping.');
      return;
    }

    var sent = [];

    if (hasMeta()) {
      try {
        window.fbq('trackCustom', name, params);
        sent.push('fbq:' + name);
        if (row.meta) {
          var metaPayload = row.metaParams ? row.metaParams(params) : params;
          if (opts && opts.eventID) {
            window.fbq('track', row.meta, metaPayload, { eventID: opts.eventID });
          } else {
            window.fbq('track', row.meta, metaPayload);
          }
          sent.push('fbq:' + row.meta);
        }
      } catch (e) { log('meta send failed', name, e); }
    }

    if (hasGa4()) {
      try {
        window.gtag('event', name, params);
        sent.push('ga4:' + name);
        if (row.ga4 && !row.ga4Auto) {
          window.gtag('event', row.ga4, row.ga4Params ? row.ga4Params(params) : params);
          sent.push('ga4:' + row.ga4);
        }
      } catch (e) { log('ga4 send failed', name, e); }
    }

    clarityTag(name, params);

    log(name, params, sent.length ? '→ ' + sent.join(', ') : '→ (no destination configured)');
  }

  window.tmTrack = tmTrack;
  window.TM = window.TM || {};
  window.TM.track = tmTrack;
  window.TM.readUtm = readUtm;

  /* ---------- shared modal scroll-lock ----------
     Freezes the page behind an open modal, IN PLACE: sets overflow:hidden on the scroll container
     (both <html> and <body> — whichever actually scrolls). overflow:hidden blocks user scroll
     (wheel/touch) while leaving the page exactly where it is, so closing never moves the page and
     there is nothing to save/restore. Paired with `overscroll-behavior:contain` on the overlay (see
     tm-design-system.css / tm-home.css) this stops the Android-Chromium (Instagram in-app browser)
     scroll-bleed the Clarity session showed. Reference-counted so overlapping locks are safe.

     NB: an earlier version used position:fixed + `window.scrollTo` restore. That JUMPS TO TOP on the
     homepage, whose root is `overflow:clip` and rejects programmatic scroll (scrollTo is a no-op) —
     so the saved position can never be restored. The in-place overflow lock avoids that entirely and
     matches the theme's existing lightbox lock (`body.tm-lb-open{overflow:hidden}`). */
  var tmScrollLocks = 0;
  window.TM.lockScroll = function () {
    if (tmScrollLocks === 0) {
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    }
    tmScrollLocks++;
  };
  window.TM.unlockScroll = function () {
    tmScrollLocks = Math.max(0, tmScrollLocks - 1);
    if (tmScrollLocks === 0) {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    }
  };

  /* Remember which CTA opened the lead modal, so tm_lead_submit can report the same placement
     after the Shopify contact form's full-page POST throws the JS state away. */
  window.TM.rememberLeadPlacement = function (placement) {
    try { sessionStorage.setItem(LEAD_PLACEMENT_KEY, placement || ''); } catch (e) {}
  };
  window.TM.takeLeadPlacement = function () {
    var v = '';
    try {
      v = sessionStorage.getItem(LEAD_PLACEMENT_KEY) || '';
      sessionStorage.removeItem(LEAD_PLACEMENT_KEY);
    } catch (e) {}
    return v || 'unknown';
  };

  /* ---------- Meta Conversions API (server-side lead) ----------
     Why: the browser pixel alone loses conversions to iOS/ITP (the _fbp cookie is capped at
     7 days) and to ad blockers, which makes CAC read worse than it is. The Worker adds the
     client IP + user-agent, which a browser cannot supply trustworthily.
     PRIVACY: the phone is hashed HERE. Only a SHA-256 digest crosses the network to our
     Worker — raw PII stays between the visitor and Shopify. */

  function readCookie(name) {
    var m = document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)');
    return m ? m.pop() : '';
  }

  /* Indian mobile → E.164 digits, no '+' (what Meta expects before hashing).
     Handles the three shapes the form actually receives: 10-digit, 0-prefixed, +91-prefixed. */
  function normalisePhoneIN(raw) {
    var d = String(raw || '').replace(/\D/g, '');
    if (!d) return '';
    if (d.length === 12 && d.indexOf('91') === 0) return d;
    if (d.length === 11 && d.charAt(0) === '0') return '91' + d.slice(1);
    if (d.length === 10) return '91' + d;
    return d; // best effort — a wrong shape lowers match quality, it never breaks the send
  }

  function sha256Hex(str) {
    if (!window.crypto || !window.crypto.subtle) return Promise.resolve('');
    var bytes = new TextEncoder().encode(str);
    return window.crypto.subtle.digest('SHA-256', bytes).then(function (buf) {
      return Array.prototype.map.call(new Uint8Array(buf), function (b) {
        return ('0' + b.toString(16)).slice(-2);
      }).join('');
    }).catch(function () { return ''; });
  }

  function newEventId() {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
    return 'tm-' + String(Math.floor(Math.random() * 1e12)) + '-' + String(performance.now() | 0);
  }

  /* Fire-and-forget: a CAPI failure must never affect the page or the browser-side pixel. */
  function sendCapi(payload) {
    if (!CFG.capiEndpoint) { log('capi skipped — no endpoint configured'); return; }
    fetch(CFG.capiEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true
    }).then(function (r) { return r.json().catch(function () { return {}; }); })
      .then(function (j) { log('capi →', payload.event_name, j); })
      .catch(function (e) { log('capi failed', e); });
  }

  /* The full server-side leg for one conversion. Returns the event_id so the caller can
     hand the SAME id to tmTrack() for deduplication. */
  window.TM.sendLeadCapi = function (opts) {
    opts = opts || {};
    var eventId = opts.eventId || newEventId();
    var phone = normalisePhoneIN(opts.phone);
    var finish = function (ph) {
      sendCapi({
        event_name: 'Lead',
        event_id: eventId,
        ph: ph || undefined,
        fbp: readCookie('_fbp'),
        fbc: readCookie('_fbc'),
        event_source_url: location.href,
        content_name: opts.placement || ''
      });
    };
    if (phone) { sha256Hex(phone).then(finish); } else { finish(''); }
    return eventId;
  };
  window.TM.newEventId = newEventId;
  window.TM._normalisePhoneIN = normalisePhoneIN; // exported for the test harness

  /* ---------- OTP (lead + call verification, via the Worker → APITxT) ----------
     The Worker proxies APITxT (a SEND-ONLY API) and owns code generation / storage / verification.
     The browser never sees the provider key — it only ever talks to our own Worker.
     If no endpoint is configured (settings.otp_endpoint blank), BOTH calls fall back to a local stub
     (any 4+ digit code passes) so the site behaves exactly as it did before OTP shipped. That means
     this can go live with the setting blank and be switched on later by filling it in — but ONLY once
     the Worker's /otp routes are deployed, or a configured-but-missing route would break the flow. */
  function otpPost(path, body) {
    return fetch(CFG.otpEndpoint + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) { return r.json().catch(function () { return {}; }); });
  }
  /* Ask the Worker to text a code to `phone`. Resolves { ok, rateLimited } so callers can tell a genuine
     failure ("check the number") apart from the 429 rate-limit ("too many — wait a minute"). */
  window.TM.otpSend = function (phone) {
    if (!CFG.otpEndpoint) { log('otp send stubbed — no endpoint configured'); return Promise.resolve({ ok: true }); }
    return otpPost('/send', { phone: phone })
      .then(function (j) { log('otp send →', j); return { ok: !!(j && j.ok), rateLimited: !!(j && j.error === 'rate limited') }; })
      .catch(function (e) { log('otp send failed', e); return { ok: false, rateLimited: false }; });
  };
  /* Check the `code` the user typed. Resolves true ONLY on a server-confirmed match. */
  window.TM.otpVerify = function (phone, code) {
    if (!CFG.otpEndpoint) { return Promise.resolve(String(code || '').replace(/\D/g, '').length >= 4); }
    return otpPost('/verify', { phone: phone, code: code })
      .then(function (j) { log('otp verify →', j); return !!(j && j.verified); })
      .catch(function (e) { log('otp verify failed', e); return false; });
  };

  /* ---------- page-view events ----------
     Fired from server-resolved context (TM_ANALYTICS.page), never sniffed from the DOM. */
  function trackPageView() {
    if (CFG.page === 'home') {
      tmTrack('tm_home_view', {});
    } else if (CFG.page === 'pdp') {
      var pr = product();
      tmTrack('tm_pdp_view', { sku: pr.sku, price: pr.price });
    }
  }

  /* ---------- tm_explore_click ----------
     ONE event for every Explore→PDP entry point (home card, header nav, burger menu, footer),
     distinguished by the `placement` param — NOT three events (ANALYTICS.md rule 1).
     Delegated + capture phase so it fires before the browser starts unloading the page.
     Markup contract: data-tm-explore="<placement>" on the link, optional data-tm-sku. */
  function bindExploreClicks() {
    document.addEventListener('click', function (e) {
      var el = e.target && e.target.closest ? e.target.closest('[data-tm-explore]') : null;
      if (!el) return;
      tmTrack('tm_explore_click', {
        sku: el.getAttribute('data-tm-sku') || '',
        placement: el.getAttribute('data-tm-explore') || ''
      });
    }, true);
  }

  function init() {
    captureUtm();
    bindExploreClicks();
    trackPageView();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
